import analyticsHelper from './analytics';
import { mapState } from 'vuex';

// eslint-disable-next-line import/no-mutable-exports
export let initialPageLoad = true;

/**
 *
 * Mixin to capture timings for 'page' components and send them to Vue.
 * Timings are captured in three scenarios:
 *
 * 1. A page calls pagePerf_signalDataLoadStart() or pagePerf_signalDataLoadAlreadyInProgress(),
 * followed by pagePerf_signalDataLoadEnd(), and then Vue signals an update(). In this case, the
 * page load is tracked with the data load times. This will account for the majority of cases where
 * the page loads its own data and is not inside a keep-alive, or is a keep-alive being loaded for
 * the first time. Pages which potentially load data prior to creation should call
 * pagePerf_signalDataLoadAlreadyInProgress() from created() if the data started loading but was not
 * finished loading prior to creation.
 *
 * 2. A page calls pagePerf_signalStartTrackingWithoutDataLoad(), and then Vue signals an update().
 * In this case, the page load is tracked without data load times. This is to account for pages
 * which have no data to be loaded, or had all the data loaded prior to being created. Pages which
 * potentially load data prior to creation should call pagePerf_signalStartTrackingWithoutDataLoad()
 * from created() if the data was fully loaded prior to creation.
 *
 * 3. A page that already went through scenario 1 or 2 is later activated(). In this case, the page
 * load is tracked without data load times. This is to account for pages which have keep-alive
 * enabled and are being displayed again.
 *
 * Special tracking is applied on the initial page load, to capture timing from the browser for
 * domain resolution, TCP/IP connection, request, response, DOM processing and DOM load prior to
 * when Vue takes over.
 *
 * You can call pagePerf_update() to signal that the page might be fully rendered.  It is safe to
 * call this function repeatedly; only the first call after the data is loaded will be tracked.
 */
export default {
    created() {
        // no need for these to be reactive, so just set these in beforeCreate
        this.$_pagePerf_dataIsLoaded = false;
        this.$_pagePerf_resetPerformanceMeasurements();
    },

    deactivated() {
        this.$_pagePerf_resetPerformanceMeasurements();
    },

    activated() {
        this.pagePerf_activate();
    },

    updated() {
        this.pagePerf_update();
    },

    computed: {
        ...mapState({
            $_pagePerf_route: (state) => state.route,
        }),
    },

    methods: {
        pagePerf_signalDataLoadStart() {
            this.$_pagePerf_startTracking();
            this.$_pagePerf_tracking.dataLoadStart = performance.now();
            this.$_pagePerf_dataIsLoaded = false;
            this.$_pagePerf_dataIsLoading = true;
            this.$_pagePerf_noDataLoad = false;
            performance.mark('pagePerformance_dataLoadStart');
        },

        pagePerf_signalDataLoadAlreadyInProgress() {
            this.pagePerf_signalDataLoadStart();
            this.$_pagePerf_tracking.dataLoadAlreadyInProgress = true;
        },

        pagePerf_signalDataLoadEnd(recordsReturned) {
            if (!this.$_pagePerf_tracking || !this.$_pagePerf_dataIsLoading) {
                console.error('pagePerf_signalDataLoadEnd() called before pagePerf_signalDataLoadStart()!');

                return;
            }

            this.$_pagePerf_dataIsLoaded = true;
            this.$_pagePerf_dataIsLoading = false;
            this.$_pagePerf_pageIsRendered = false;
            this.$_pagePerf_tracking.dataLoadEnd = performance.now();
            this.$_pagePerf_tracking.recordsReturned = recordsReturned;
            performance.mark('pagePerformance_dataLoadEnd');

            this.$_pagePerf_observer = new PerformanceObserver((() => {
                this.pagePerf_update();
                this.$_pagePerf_observer.disconnect();
            }));

            this.$_pagePerf_observer.observe({ type: 'paint', buffered: true });
        },

        pagePerf_signalStartTrackingWithoutDataLoad() {
            this.$_pagePerf_startTracking();
            this.$_pagePerf_dataIsLoaded = false;
            this.$_pagePerf_dataIsLoading = false;
            this.$_pagePerf_noDataLoad = true;
            this.$_pagePerf_pageIsRendered = false;
        },

        pagePerf_activate() {
            // This is a keep-alive component that isn't rendered yet
            if (!this.$_pagePerf_pageIsRendered) {
                if (this.$_pagePerf_dataIsLoaded || this.$_pagePerf_noDataLoad) {
                    // Page being shown when data was already loaded previously, or no data
                    this.pagePerf_signalStartTrackingWithoutDataLoad();
                    this.$_pagePerf_trackPageRenderedWithNoDataLoading();
                }
            }
        },

        pagePerf_update() {
            if (this.$_pagePerf_tracking && !this.$_pagePerf_pageIsRendered) {
                if (this.$_pagePerf_noDataLoad) {
                    // The first update after the component was activated or created with preloaded data
                    this.$_pagePerf_trackPageRenderedWithNoDataLoading();
                } else if (this.$_pagePerf_dataIsLoaded) {
                    // The first update after data was loaded
                    this.$_pagePerf_trackPageRenderedWithDataLoaded();
                }
            }
        },

        $_pagePerf_startTracking() {
            performance.mark('pagePerformance_trackingStart');

            // initialize $_pagePerf_tracking and capture the last route time
            this.$_pagePerf_tracking = {
                routeStart: this.$_pagePerf_route.meta.routeStartTime,
                trackingStart: performance.now(),
            };

            if (initialPageLoad) {
                initialPageLoad = false;
                // Capture the related (most recent) navigation event
                this.$_pagePerf_tracking.navTiming = performance.getEntriesByType('navigation').pop() || {};
            }
        },

        $_pagePerf_trackPageRenderedWithDataLoaded() {
            this.$_pagePerf_pageIsRendered = true;
            this.$nextTick(() => {
                this.$_pagePerf_tracking.pageRenderEnd = performance.now();

                const routeTime = this.$_pagePerf_tracking.dataLoadStart - this.$_pagePerf_tracking.routeStart;
                const dataLoadTime = this.$_pagePerf_tracking.dataLoadEnd - this.$_pagePerf_tracking.dataLoadStart;
                const renderTime = this.$_pagePerf_tracking.pageRenderEnd - this.$_pagePerf_tracking.dataLoadEnd;
                const routeAndRenderTime = this.$_pagePerf_tracking.pageRenderEnd - this.$_pagePerf_tracking.routeStart;

                this.$_pagePerf_trackPerformanceEvent({
                    loadType: 'loadWithData',
                    routeTime,
                    dataLoadTime,
                    renderTime,
                    routeAndRenderTime,
                    dataLoadAlreadyInProgress: Boolean(this.$_pagePerf_tracking.dataLoadAlreadyInProgress),
                    recordsReturned: this.$_pagePerf_tracking.recordsReturned,
                });

                this.$_pagePerf_resetPerformanceMeasurements();
            });
        },

        $_pagePerf_trackPageRenderedWithNoDataLoading() {
            this.$_pagePerf_pageIsRendered = true;
            this.$nextTick(function () {
                this.$_pagePerf_tracking.pageRenderEnd = performance.now();

                const routeTime = this.$_pagePerf_tracking.trackingStart - this.$_pagePerf_tracking.routeStart;
                const renderTime = this.$_pagePerf_tracking.pageRenderEnd - this.$_pagePerf_tracking.trackingStart;
                const routeAndRenderTime = this.$_pagePerf_tracking.pageRenderEnd - this.$_pagePerf_tracking.routeStart;

                this.$_pagePerf_trackPerformanceEvent({
                    loadType: 'loadWithoutData',
                    routeTime,
                    renderTime,
                    routeAndRenderTime,
                });

                this.$_pagePerf_resetPerformanceMeasurements();
            });
        },

        $_pagePerf_trackPerformanceEvent(eventProps) {
            const initialPageLoadTracked = Boolean(this.$_pagePerf_tracking.navTiming);

            eventProps = {
                ...eventProps,
                nonInteraction: 1,
                initialPageLoad: initialPageLoadTracked,
                objectId: this.$_pagePerf_route.params.id,
                route: this.$_pagePerf_route.name,
            };

            if (initialPageLoadTracked) {
                // Initial page loads get additional tracking
                const { navTiming } = this.$_pagePerf_tracking;
                const { startTime } = navTiming;

                // Timings on single actions
                eventProps.domainLookupTime = navTiming.domainLookupEnd - navTiming.domainLookupStart;
                eventProps.connectTime = navTiming.connectEnd - navTiming.connectStart;
                eventProps.requestTime = navTiming.responseStart - navTiming.requestStart;
                eventProps.responseTime = navTiming.responseEnd - navTiming.responseStart;
                eventProps.domProcessingTime = navTiming.loadEventStart - navTiming.responseEnd;
                eventProps.domLoadTime = navTiming.loadEventEnd - navTiming.loadEventStart;

                // Timings of end of actions vs startTime
                eventProps.domContentLoadedTime = navTiming.domContentLoadedEventEnd - startTime;
                eventProps.domCompleteTime = navTiming.domComplete - startTime;
                eventProps.domLoadedTime = navTiming.loadEventEnd - startTime;
                eventProps.initialLoadAndRenderTime = this.$_pagePerf_tracking.pageRenderEnd - startTime;
                eventProps.navigationType = navTiming.type;
                performance.mark('pagePerformance_pageRenderedWithInitialLoad');
            } else {
                performance.mark('pagePerformance_pageRendered');
            }

            // console.log('$_pagePerf_trackPerformanceEvent', eventProps, this.$_pagePerf_tracking, this.$el);

            analyticsHelper.trackEvent({
                category: 'Performance',
                subject: 'Page Performance',
                verb: 'Tracked',
                label: this.$_pagePerf_route.name,
                eventProps,
            });
        },

        $_pagePerf_resetPerformanceMeasurements() {
            this.$_pagePerf_tracking = null;
            this.$_pagePerf_pageIsRendered = false;
        },
    },
};
